import * as React from 'react';

import { parseDate, today, getLocalTimeZone } from '@internationalized/date';
import {
  enumCurrencyCode,
  enumInvoicePricingBasis,
  enumUnit,
  InvoicePricingBasis,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  ConsoleDialog,
  TextField,
  TextArea,
  Select,
  Switch,
  DatePicker,
  NumberField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { Check } from 'lucide-react';

import {
  createInboundTollingAgreementAtom,
  updateInboundTollingAgreementAtom,
} from '../../_graphql/mutationInboundTollingAgreement';
import { $remoteAllProcessingTypes } from '../../_state/processingTypes.state';
import { useTranslations } from '../../i18n/useTranslations';
import { InboundTollingAgreementRecord } from '../../types/inboundTollingAgreement';

import { SelectAccount } from '~/shared/components/select-fields/SelectAccount';
import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';

type InboundTollingDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editingAgreement?: InboundTollingAgreementRecord | null;
};

export const InboundTollingDialog = ({
  isOpen,
  onClose,
  onSuccess,
  editingAgreement,
}: InboundTollingDialogProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [agreementNumber, setAgreementNumber] = React.useState('');
  const [customerAccountId, setCustomerAccountId] = React.useState<
    string | null
  >(null);
  const [processingTypeId, setProcessingTypeId] = React.useState('');
  const [effectiveDate, setEffectiveDate] = React.useState<string | null>(null);
  const [expirationDate, setExpirationDate] = React.useState<string | null>(
    null,
  );
  const [pricePerUnit, setPricePerUnit] = React.useState<number | null>(null);
  const [priceUnit, setPriceUnit] = React.useState<string | null>(null);
  const [expectedYieldPercentage, setExpectedYieldPercentage] = React.useState<
    number | null
  >(null);
  const [terms, setTerms] = React.useState('');
  const [isActive, setIsActive] = React.useState(true);
  const [invoicePricingBasis, setInvoicePricingBasis] =
    React.useState<InvoicePricingBasis>(enumInvoicePricingBasis.Output);
  const [facilityId, setFacilityId] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: processingTypesData } = useAtomValue($remoteAllProcessingTypes);
  const processingTypes = React.useMemo(
    () => processingTypesData?.processingTypes ?? [],
    [processingTypesData?.processingTypes],
  );

  const createInboundTollingAgreement = useSetAtom(
    createInboundTollingAgreementAtom({}),
  );
  const updateInboundTollingAgreement = useSetAtom(
    updateInboundTollingAgreementAtom({}),
  );

  const isEditMode = !!editingAgreement;

  React.useEffect(() => {
    if (editingAgreement) {
      setAgreementNumber(editingAgreement.agreementNumber);
      setCustomerAccountId(editingAgreement.customerAccountId);
      setProcessingTypeId(editingAgreement.processingTypeId);
      setEffectiveDate(editingAgreement.effectiveDate);
      setExpirationDate(editingAgreement.expirationDate || null);
      setPricePerUnit(editingAgreement.pricePerUnitAmount || null);
      setPriceUnit(editingAgreement.priceUnit || null);
      setExpectedYieldPercentage(
        editingAgreement.expectedYieldPercentage || null,
      );
      setTerms(editingAgreement.terms || '');
      setIsActive(editingAgreement.isActive);
      setInvoicePricingBasis(editingAgreement.invoicePricingBasis);
      setFacilityId(editingAgreement.facilityId);
    } else {
      setAgreementNumber('');
      setCustomerAccountId(null);
      setProcessingTypeId('');
      setEffectiveDate(null);
      setExpirationDate(null);
      setPricePerUnit(null);
      setPriceUnit(null);
      setExpectedYieldPercentage(null);
      setTerms('');
      setIsActive(true);
      setInvoicePricingBasis(enumInvoicePricingBasis.Output);
      setFacilityId(null);
    }
  }, [editingAgreement]);

  const handleClose = React.useCallback(() => {
    setAgreementNumber('');
    setCustomerAccountId(null);
    setProcessingTypeId('');
    setEffectiveDate(null);
    setExpirationDate(null);
    setPricePerUnit(null);
    setPriceUnit(null);
    setExpectedYieldPercentage(null);
    setTerms('');
    setIsActive(true);
    setInvoicePricingBasis(enumInvoicePricingBasis.Output);
    setFacilityId(null);
    onClose();
  }, [onClose]);

  const handleSubmit = React.useCallback(async () => {
    if (!agreementNumber.trim()) {
      toast.error(
        t.tollProcessingAgreementsView.dialog.validation
          .agreementNumberRequired,
      );
      return;
    }

    if (!isEditMode && !customerAccountId) {
      toast.error(
        t.tollProcessingAgreementsView.dialog.validation.customerRequired,
      );
      return;
    }

    if (!isEditMode && !processingTypeId) {
      toast.error(
        t.tollProcessingAgreementsView.dialog.validation.processingTypeRequired,
      );
      return;
    }

    if (!isEditMode && !effectiveDate) {
      toast.error(
        t.tollProcessingAgreementsView.dialog.validation.effectiveDateRequired,
      );
      return;
    }

    try {
      setIsSubmitting(true);

      if (isEditMode && editingAgreement) {
        await updateInboundTollingAgreement({
          id: editingAgreement.id,
          agreementNumber: agreementNumber.trim(),
          expirationDate: expirationDate || undefined,
          pricePerUnit:
            pricePerUnit !== null
              ? { amount: pricePerUnit, currencyCode: enumCurrencyCode.USD }
              : undefined,
          priceUnit: priceUnit || undefined,
          expectedYieldPercentage: expectedYieldPercentage || undefined,
          terms: terms.trim() || undefined,
          isActive,
          invoicePricingBasis,
        });
        toast.success(t.tollProcessingAgreementsView.dialog.updateSuccess);
      } else if (customerAccountId && effectiveDate && facilityId) {
        await createInboundTollingAgreement({
          agreementNumber: agreementNumber.trim(),
          customerAccountId,
          processingTypeId,
          effectiveDate,
          expirationDate: expirationDate || undefined,
          pricePerUnit:
            pricePerUnit !== null
              ? { amount: pricePerUnit, currencyCode: enumCurrencyCode.USD }
              : undefined,
          priceUnit: priceUnit || undefined,
          expectedYieldPercentage: expectedYieldPercentage || undefined,
          terms: terms.trim() || undefined,
          isActive,
          invoicePricingBasis,
          facilityId,
        });
        toast.success(t.tollProcessingAgreementsView.dialog.createSuccess);
      }

      onSuccess?.();
      handleClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred';
      toast.error(
        isEditMode
          ? t.tollProcessingAgreementsView.dialog.updateError(errorMessage)
          : t.tollProcessingAgreementsView.dialog.createError(errorMessage),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    agreementNumber,
    customerAccountId,
    processingTypeId,
    effectiveDate,
    expirationDate,
    pricePerUnit,
    priceUnit,
    expectedYieldPercentage,
    terms,
    isActive,
    invoicePricingBasis,
    facilityId,
    isEditMode,
    editingAgreement,
    createInboundTollingAgreement,
    updateInboundTollingAgreement,
    toast,
    t,
    onSuccess,
    handleClose,
  ]);

  const processingTypeOptions = React.useMemo(
    () => processingTypes.map((pt) => ({ key: pt.id, label: pt.name })),
    [processingTypes],
  );

  const canSubmit =
    Boolean(agreementNumber.trim()) &&
    (isEditMode || Boolean(customerAccountId)) &&
    (isEditMode || Boolean(processingTypeId)) &&
    (isEditMode || Boolean(effectiveDate)) &&
    (isEditMode || Boolean(facilityId)) &&
    !isSubmitting;

  const dialogTitle = isEditMode
    ? t.tollProcessingAgreementsView.dialog.editTitle
    : t.tollProcessingAgreementsView.dialog.createTitle;

  const submitButtonText = isEditMode
    ? isSubmitting
      ? t.tollProcessingAgreementsView.dialog.updating
      : t.tollProcessingAgreementsView.dialog.update
    : isSubmitting
      ? t.tollProcessingAgreementsView.dialog.creating
      : t.tollProcessingAgreementsView.dialog.create;

  return (
    <ConsoleDialog
      className="@InboundTollingDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={dialogTitle}
      size="wrap"
      content={
        <div className="space-y-4">
          <TextField
            label={t.tollProcessingAgreementsView.dialog.agreementNumberLabel}
            value={agreementNumber}
            onChange={setAgreementNumber}
            placeholder={
              t.tollProcessingAgreementsView.dialog.agreementNumberPlaceholder
            }
            isRequired
          />

          {!isEditMode && (
            <>
              <SelectAccount
                label={t.tollProcessingAgreementsView.dialog.customerLabel}
                value={customerAccountId}
                onChange={(value) => setCustomerAccountId(value ?? null)}
                placeholder={
                  t.tollProcessingAgreementsView.dialog.customerPlaceholder
                }
                isRequired
              />

              <Select
                label={
                  t.tollProcessingAgreementsView.dialog.processingTypeLabel
                }
                value={processingTypeId}
                onChange={(key) => setProcessingTypeId(String(key))}
                options={processingTypeOptions}
                placeholder={
                  t.tollProcessingAgreementsView.dialog
                    .processingTypePlaceholder
                }
                isRequired
              />

              <SelectFacility
                label={t.tollProcessingAgreementsView.dialog.facilityLabel}
                value={facilityId}
                onChange={(value) => setFacilityId(value ?? null)}
                isRequired
              />
            </>
          )}

          <div className="grid grid-cols-2 gap-4">
            <DatePicker
              label={t.tollProcessingAgreementsView.dialog.effectiveDateLabel}
              value={
                effectiveDate ? parseDate(effectiveDate.split('T')[0]) : null
              }
              onChange={(date) =>
                setEffectiveDate(date ? date.toString() : null)
              }
              minValue={today(getLocalTimeZone())}
              isDisabled={isEditMode}
            />
            <DatePicker
              label={t.tollProcessingAgreementsView.dialog.expirationDateLabel}
              value={
                expirationDate ? parseDate(expirationDate.split('T')[0]) : null
              }
              onChange={(date) =>
                setExpirationDate(date ? date.toString() : null)
              }
              minValue={
                effectiveDate
                  ? parseDate(effectiveDate.split('T')[0])
                  : today(getLocalTimeZone())
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <NumberField
              label={t.tollProcessingAgreementsView.dialog.pricePerUnitLabel}
              value={pricePerUnit ?? undefined}
              onChange={(val) => setPricePerUnit(val ?? null)}
              formatOptions={{ style: 'currency', currency: 'USD' }}
              minValue={0}
            />
            <SelectEnum
              label={t.tollProcessingAgreementsView.dialog.priceUnitLabel}
              value={priceUnit ?? undefined}
              onChange={(value) => setPriceUnit(value ?? null)}
              enumValue={enumUnit}
              placeholder={
                t.tollProcessingAgreementsView.dialog.priceUnitPlaceholder
              }
            />
          </div>

          <NumberField
            label={t.tollProcessingAgreementsView.dialog.expectedYieldLabel}
            value={expectedYieldPercentage ?? undefined}
            onChange={(val) => setExpectedYieldPercentage(val ?? null)}
            minValue={0}
            maxValue={100}
            formatOptions={{ style: 'percent', maximumFractionDigits: 1 }}
          />

          <SelectEnum
            label={
              t.tollProcessingAgreementsView.dialog.invoicePricingBasisLabel
            }
            value={invoicePricingBasis}
            onChange={(value) =>
              setInvoicePricingBasis(
                (value as InvoicePricingBasis) ??
                  enumInvoicePricingBasis.Output,
              )
            }
            enumValue={enumInvoicePricingBasis}
          />

          <TextArea
            label={t.tollProcessingAgreementsView.dialog.termsLabel}
            value={terms}
            onChange={setTerms}
            placeholder={t.tollProcessingAgreementsView.dialog.termsPlaceholder}
          />

          <div className="flex items-center gap-2">
            <Switch isSelected={isActive} onChange={setIsActive} />
            <span className="text-sm">
              {t.tollProcessingAgreementsView.dialog.isActiveLabel}
            </span>
          </div>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.tollProcessingAgreementsView.dialog.cancel}
          </Button>
          <Button
            icon={<Check />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
          >
            {submitButtonText}
          </Button>
        </div>
      }
    />
  );
};
